import { type ReactNode } from 'react';
import { useContentScanners } from '../../client/content-scanners';
import { formatFileLocation } from '@reflex/shared';
import { matchGroups, matchWholeTextScanner } from './markdown/content-scanner-dispatch';
import { absoluteFilePath, useWorkingDir } from './WorkingDirContext';

interface ScannedPathProps {
  /**
   * Path to render as an interactive affordance. A relative one resolves
   * against the surrounding {@link WorkingDirProvider}'s directory, since that
   * is how an agent writes the paths a caller has in hand.
   */
  path: string;
  /**
   * 1-based first line of the region worth reading. Composed onto the path as
   * the `path:line` suffix before matching, so the resolved scanner receives
   * one token and can open its preview at that line.
   */
  startLine?: number;
  /** 1-based last line, inclusive. Ignored without a `startLine`. */
  endLine?: number;
  /**
   * Visible label. Defaults to the full path (plus the line range, when one is
   * given). Passed to the resolved scanner component as `children`, so e.g. the
   * filesystem path chip can show a short basename while still previewing the
   * full path.
   */
  label?: ReactNode;
}

/**
 * Render a known file path through the content-scanner registry.
 *
 * `MarkdownContent` discovers paths inside free text and swaps them for the
 * matching scanner component (the filesystem file-path popover, today). This
 * component does the same for a path we already have in hand — a tool-call
 * diff header, a file list row — without any surface having to import the
 * plugin that owns the popover. The registry is the cross-plugin seam.
 *
 * A caller that knows *which lines* matter passes them as `startLine` /
 * `endLine` rather than pre-formatting them into `path`: the `path:line`
 * convention is spelled once, in `@reflex/ui/client/file-location`, so the
 * suffix this builds is exactly the one the scanner's regex expects and its
 * preview parses back out.
 *
 * Degrades cleanly: when no `text` scanner claims the whole token (no plugin
 * registered, a relative path with no working directory to resolve against, a
 * scanner that predates line suffixes), it renders the label as plain text.
 */
export function ScannedPath({ path, startLine, endLine, label }: ScannedPathProps) {
  // Subscribe so a late-registering plugin scanner re-renders this in place.
  useContentScanners();
  const workingDir = useWorkingDir();
  // Scanners only claim absolute paths, and the paths a caller has in hand are
  // usually written relative to wherever the agent ran. Resolve first so the
  // label can keep saying what the tool said while the token the scanner sees
  // names a real file. Unresolvable paths fall through and render as plain text.
  const resolved = absoluteFilePath(path, workingDir) ?? path;
  const located = formatFileLocation({ path: resolved, startLine, endLine });
  // A scanner that claims the bare path but not the located token is one that
  // predates line suffixes. Falling back keeps the preview reachable; the
  // label still names the region, so nothing is lost but the scroll.
  const hit =
    matchWholeTextScanner(located) ??
    (located === resolved ? null : matchWholeTextScanner(resolved));

  if (!hit) return <>{label ?? located}</>;

  const Component = hit.scanner.Component;
  return (
    <Component match={hit.match} groups={matchGroups(hit.scanner.regex, hit.match)}>
      {label ?? located}
    </Component>
  );
}
